import {
  createSaveEnvelope,
  normalizeSaveEnvelope,
  verifySaveEnvelope,
} from './SaveEnvelope.js';
import { BrowserSaveStorage, normalizeSaveSlotId } from './BrowserSaveStorage.js';

export const SAVE_SYSTEM_VERSION = 1;

const clone = (value) => value === undefined
  ? undefined
  : JSON.parse(JSON.stringify(value));

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) deepFreeze(child);
  return Object.freeze(value);
}

function assertMethod(value, method, name) {
  if (!value || typeof value[method] !== 'function') {
    throw new TypeError(`${name} must provide ${method}()`);
  }
}

function assertFunction(value, name) {
  if (typeof value !== 'function') throw new TypeError(`${name} must be a function`);
  return value;
}

function assertTitle(value) {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError('title must be a non-empty string');
  }
  return value.trim().slice(0, 120);
}

function assertTimestamp(value, name) {
  if (typeof value !== 'string' || Number.isNaN(Date.parse(value))) {
    throw new TypeError(`${name} must return a valid timestamp`);
  }
  return new Date(value).toISOString();
}

function sameJson(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function assertSnapshotChecksum(actual, expected, name) {
  const actualChecksum = actual?.checksum ?? null;
  const expectedChecksum = expected?.checksum ?? null;
  if (actualChecksum !== expectedChecksum) {
    throw new RangeError(`${name} checksum did not restore exactly`);
  }
}

export class SaveSystem {
  constructor({
    clock,
    random,
    simulationSnapshots,
    entityWorld,
    traversalWorld,
    snakeState,
    wildlifeSystem,
    huntingSystem,
    storySystem,
    storage = new BrowserSaveStorage(),
    captureEnvironment,
    restoreEnvironment,
    afterLoad = () => {},
    now = () => new Date().toISOString(),
    buildInfo = {},
  }) {
    assertMethod(clock, 'snapshot', 'clock');
    assertMethod(clock, 'reset', 'clock');
    assertMethod(random, 'snapshot', 'random');
    assertMethod(random, 'restore', 'random');
    assertMethod(simulationSnapshots, 'getStatus', 'simulationSnapshots');
    assertMethod(simulationSnapshots, 'reset', 'simulationSnapshots');
    for (const [name, value] of Object.entries({
      entityWorld,
      traversalWorld,
      wildlifeSystem,
      huntingSystem,
      storySystem,
    })) {
      assertMethod(value, 'snapshot', name);
      assertMethod(value, 'restore', name);
    }
    assertMethod(snakeState, 'snapshot', 'snakeState');
    assertMethod(snakeState, 'restore', 'snakeState');
    assertMethod(snakeState, 'reset', 'snakeState');
    for (const method of ['list', 'read', 'write', 'delete', 'clear', 'getStatus']) {
      assertMethod(storage, method, 'storage');
    }

    this.clock = clock;
    this.random = random;
    this.simulationSnapshots = simulationSnapshots;
    this.entityWorld = entityWorld;
    this.traversalWorld = traversalWorld;
    this.snakeState = snakeState;
    this.wildlifeSystem = wildlifeSystem;
    this.huntingSystem = huntingSystem;
    this.storySystem = storySystem;
    this.storage = storage;
    this.captureEnvironment = assertFunction(captureEnvironment, 'captureEnvironment');
    this.restoreEnvironment = assertFunction(restoreEnvironment, 'restoreEnvironment');
    this.afterLoad = assertFunction(afterLoad, 'afterLoad');
    this.now = assertFunction(now, 'now');
    this.buildInfo = clone(buildInfo) ?? {};
    this.revision = 0;
    this.counters = {
      saves: 0,
      loads: 0,
      imports: 0,
      deletes: 0,
      failures: 0,
    };
    this.lastOperation = null;
  }

  #captureSession() {
    return {
      environment: clone(this.captureEnvironment()),
      clock: clone(this.clock.snapshot()),
      random: clone(this.random.snapshot()),
      simulation: {
        nextTick: this.simulationSnapshots.getStatus().nextTick,
      },
      entityWorld: clone(this.entityWorld.snapshot()),
      traversalWorld: clone(this.traversalWorld.snapshot()),
      snakeState: clone(this.snakeState.snapshot()),
      wildlifeSystem: clone(this.wildlifeSystem.snapshot()),
      huntingSystem: clone(this.huntingSystem.snapshot()),
      storySystem: clone(this.storySystem.snapshot()),
    };
  }

  #metadata(title, metadata, session) {
    const story = session.storySystem;
    const snake = session.snakeState;
    return {
      ...(clone(metadata) ?? {}),
      title,
      mode: story.mode,
      activeMissionId: story.activeMissionId,
      completedMissionCount: story.missions.filter((mission) => (
        mission.status === 'completed'
      )).length,
      snakeSpecies: snake?.species ?? null,
      snakeLifecycle: snake?.lifecycle ?? null,
      tick: session.simulation.nextTick,
      simulationTime: session.clock.simulationTime,
      buildChannel: this.buildInfo.channel ?? null,
      buildCommit: this.buildInfo.commit ?? null,
    };
  }

  #record(type, details = {}, counter = null) {
    this.revision += 1;
    if (counter) this.counters[counter] += 1;
    this.lastOperation = deepFreeze({
      type,
      revision: this.revision,
      ...clone(details),
    });
    return this.lastOperation;
  }

  #validateCompatibility(session) {
    const currentStepSeconds = this.clock.snapshot().stepSeconds;
    if (session.clock.stepSeconds !== currentStepSeconds) {
      throw new RangeError(
        `Save stepSeconds ${session.clock.stepSeconds} does not match runtime ${currentStepSeconds}`,
      );
    }
  }

  #restoreSession(session) {
    this.#validateCompatibility(session);

    this.entityWorld.restore(session.entityWorld);
    this.traversalWorld.restore(session.traversalWorld);

    if (session.snakeState === null) this.snakeState.reset({ removeEntity: false });
    else this.snakeState.restore(session.snakeState);
    this.wildlifeSystem.restore(session.wildlifeSystem);
    this.huntingSystem.restore(session.huntingSystem);
    this.storySystem.restore(session.storySystem);

    // Snake and wildlife restoration synchronize their mirrors. Reapply the
    // saved authority snapshots last so revisions and checksums remain exact.
    this.entityWorld.restore(session.entityWorld);
    this.traversalWorld.restore(session.traversalWorld);

    this.random.restore(session.random);
    this.simulationSnapshots.reset({ nextTick: session.simulation.nextTick });
    this.clock.reset({
      simulationTime: session.clock.simulationTime,
      timeScale: session.clock.timeScale,
      paused: session.clock.paused,
    });
    this.restoreEnvironment(clone(session.environment));

    assertSnapshotChecksum(this.entityWorld.snapshot(), session.entityWorld, 'entityWorld');
    assertSnapshotChecksum(this.traversalWorld.snapshot(), session.traversalWorld, 'traversalWorld');
    const restoredSnake = this.snakeState.snapshot();
    if (session.snakeState === null) {
      if (restoredSnake !== null) throw new RangeError('snakeState did not restore to empty');
    } else {
      assertSnapshotChecksum(restoredSnake, session.snakeState, 'snakeState');
    }
    assertSnapshotChecksum(this.wildlifeSystem.snapshot(), session.wildlifeSystem, 'wildlifeSystem');
    assertSnapshotChecksum(this.huntingSystem.snapshot(), session.huntingSystem, 'huntingSystem');
    assertSnapshotChecksum(this.storySystem.snapshot(), session.storySystem, 'storySystem');
    if (!sameJson(this.random.snapshot(), session.random)) {
      throw new RangeError('random state did not restore exactly');
    }
    if (this.simulationSnapshots.getStatus().nextTick !== session.simulation.nextTick) {
      throw new RangeError('simulation tick did not restore exactly');
    }
    if (!sameJson(this.clock.snapshot(), session.clock)) {
      throw new RangeError('clock state did not restore exactly');
    }
    if (!sameJson(this.captureEnvironment(), session.environment)) {
      throw new RangeError('environment state did not restore exactly');
    }
  }

  captureEnvelope({
    title = 'Manual Save',
    metadata = {},
    createdAt = this.now(),
    updatedAt = createdAt,
  } = {}) {
    const normalizedTitle = assertTitle(title);
    const normalizedCreatedAt = assertTimestamp(createdAt, 'createdAt');
    const normalizedUpdatedAt = assertTimestamp(updatedAt, 'updatedAt');
    const session = this.#captureSession();
    return createSaveEnvelope({
      session,
      metadata: this.#metadata(normalizedTitle, metadata, session),
      createdAt: normalizedCreatedAt,
      updatedAt: normalizedUpdatedAt,
    });
  }

  verifyEnvelope(value) {
    return verifySaveEnvelope(value);
  }

  saveSlot(slotId, options = {}) {
    const normalizedSlotId = normalizeSaveSlotId(slotId);
    const previous = this.storage.read(normalizedSlotId);
    const previousNormalized = previous ? normalizeSaveEnvelope(previous).envelope : null;
    const timestamp = assertTimestamp(this.now(), 'now()');
    const envelope = this.captureEnvelope({
      ...options,
      createdAt: options.createdAt ?? previousNormalized?.createdAt ?? timestamp,
      updatedAt: options.updatedAt ?? timestamp,
    });
    const summary = this.storage.write(normalizedSlotId, envelope);
    this.#record('save', {
      slotId: normalizedSlotId,
      checksum: envelope.checksum,
    }, 'saves');
    return deepFreeze({ ok: true, slot: summary, envelope, status: this.getStatus() });
  }

  loadEnvelope(value, { sourceSlotId = null } = {}) {
    const normalized = normalizeSaveEnvelope(value);
    const backup = this.#captureSession();
    try {
      this.#restoreSession(normalized.envelope.session);
      this.afterLoad({
        envelope: normalized.envelope,
        sourceSlotId,
        migratedFromVersion: normalized.migratedFromVersion,
      });
    } catch (error) {
      let rollbackError = null;
      try {
        this.#restoreSession(backup);
      } catch (failure) {
        rollbackError = failure;
      }
      this.#record('load-failed', {
        slotId: sourceSlotId,
        error: error instanceof Error ? error.message : String(error),
        rollbackError: rollbackError instanceof Error ? rollbackError.message : null,
      }, 'failures');
      if (rollbackError) {
        throw new AggregateError([error, rollbackError], 'Save load and rollback both failed');
      }
      throw error;
    }

    this.#record('load', {
      slotId: sourceSlotId,
      checksum: normalized.envelope.checksum,
      migratedFromVersion: normalized.migratedFromVersion,
    }, 'loads');
    return deepFreeze({
      ok: true,
      envelope: normalized.envelope,
      migratedFromVersion: normalized.migratedFromVersion,
      status: this.getStatus(),
    });
  }

  loadSlot(slotId) {
    const normalizedSlotId = normalizeSaveSlotId(slotId);
    const value = this.storage.read(normalizedSlotId);
    if (value === null) throw new RangeError(`Save slot does not exist: ${normalizedSlotId}`);
    return this.loadEnvelope(value, { sourceSlotId: normalizedSlotId });
  }

  importSlot(slotId, value, { load = false } = {}) {
    const normalizedSlotId = normalizeSaveSlotId(slotId);
    const normalized = normalizeSaveEnvelope(value);
    const summary = this.storage.write(normalizedSlotId, normalized.envelope);
    this.#record('import', {
      slotId: normalizedSlotId,
      checksum: normalized.envelope.checksum,
      migratedFromVersion: normalized.migratedFromVersion,
    }, 'imports');
    const loaded = load
      ? this.loadEnvelope(normalized.envelope, { sourceSlotId: normalizedSlotId })
      : null;
    return deepFreeze({
      ok: true,
      slot: summary,
      envelope: normalized.envelope,
      migratedFromVersion: normalized.migratedFromVersion,
      loaded,
      status: this.getStatus(),
    });
  }

  exportSlot(slotId) {
    const normalizedSlotId = normalizeSaveSlotId(slotId);
    const value = this.storage.read(normalizedSlotId);
    if (value === null) throw new RangeError(`Save slot does not exist: ${normalizedSlotId}`);
    return JSON.stringify(normalizeSaveEnvelope(value).envelope);
  }

  listSlots() {
    return this.storage.list();
  }

  deleteSlot(slotId) {
    const normalizedSlotId = normalizeSaveSlotId(slotId);
    const deleted = this.storage.delete(normalizedSlotId);
    if (deleted) this.#record('delete', { slotId: normalizedSlotId }, 'deletes');
    return deepFreeze({ ok: deleted, slotId: normalizedSlotId, status: this.getStatus() });
  }

  clearSlots() {
    const previousCount = this.storage.getStatus().slotCount;
    const storage = this.storage.clear();
    if (previousCount > 0) {
      this.#record('clear', { deletedCount: previousCount }, 'deletes');
    }
    return deepFreeze({ ok: true, deletedCount: previousCount, storage, status: this.getStatus() });
  }

  getStatus() {
    const storage = this.storage.getStatus();
    return deepFreeze({
      version: SAVE_SYSTEM_VERSION,
      revision: this.revision,
      slotCount: storage.slotCount,
      slotIds: storage.slotIds,
      counters: clone(this.counters),
      lastOperation: clone(this.lastOperation),
    });
  }
}
